import { Injectable } from "@angular/core";

import { CipherData } from "@bitwarden/common/models/data/cipher.data";

const CACHE_KEY_PREFIX = "gloss_vault_cache_";

interface VaultSyncCache {
  lastSync: string; // ISO 8601
  ciphers: { [id: string]: CipherData };
}

@Injectable({ providedIn: "root" })
export class VaultSyncCacheService {
  // TODO: make this a user-facing setting so web users can opt out.
  // Web concern: encrypted cipher blob persists in localStorage between sessions —
  // not a practical risk (data is encrypted) but undesirable on shared machines.
  // Until the setting exists, caching is on for all platforms.
  private isCacheEnabled = true;

  saveCache(userId: string, lastSync: Date, ciphers: { [id: string]: CipherData }): void {
    if (!this.isCacheEnabled) {
      return;
    }
    try {
      const entry: VaultSyncCache = { lastSync: lastSync.toISOString(), ciphers };
      localStorage.setItem(CACHE_KEY_PREFIX + userId, JSON.stringify(entry));
    } catch {
      // Ignore storage errors (e.g. quota exceeded) — fall back to full sync next login
    }
  }

  restoreCache(userId: string): { lastSync: Date; ciphers: { [id: string]: CipherData } } | null {
    if (!this.isCacheEnabled) {
      return null;
    }
    try {
      const raw = localStorage.getItem(CACHE_KEY_PREFIX + userId);
      if (!raw) {
        return null;
      }
      const entry: VaultSyncCache = JSON.parse(raw);
      if (!entry?.lastSync || !entry?.ciphers) {
        return null;
      }
      return { lastSync: new Date(entry.lastSync), ciphers: entry.ciphers };
    } catch {
      return null;
    }
  }

  clearCache(userId: string): void {
    localStorage.removeItem(CACHE_KEY_PREFIX + userId);
  }
}
